<template>
  <div class="gs-item-list">
    <div class="gs-item-list__head">
      <span class="gs-item-list__title">{{ title }}</span>
      <span class="gs-item-list__count">{{ items.length }}</span>
    </div>

    <p v-if="!items.length" class="gs-item-list__empty">{{ emptyCopy }}</p>

    <draggable
      v-else
      :list="localItems"
      tag="div"
      class="gs-item-list__rows"
      handle=".gs-item-list__grip"
      ghost-class="is-ghost"
      :animation="150"
      @end="onReorder(localItems)"
    >
      <button
        v-for="item in localItems"
        :key="item.id"
        type="button"
        class="gs-item-list__row"
        :class="{
          'is-selected': isSelected(item),
          'has-error': hasError(item),
        }"
        :aria-current="isSelected(item) ? 'true' : null"
        @click="$emit('select', item.id)"
      >
        <span class="gs-item-list__grip" aria-hidden="true" @click.stop>
          <gs-icon name="grip-vertical" size="16"></gs-icon>
        </span>
        <span
          v-if="enabledKey"
          class="gs-item-list__dot"
          :class="{ 'is-on': !!item[enabledKey] }"
          aria-hidden="true"
        ></span>
        <span class="gs-item-list__name">{{ item.name || nameFallback }}</span>
        <gs-icon class="gs-item-list__chevron" name="chevron-down" size="14"></gs-icon>
      </button>
    </draggable>

    <button type="button" class="gs-item-list__add" data-action="add" @click="$emit('add')">
      <gs-icon name="plus" size="14"></gs-icon>
      <span>{{ addLabel }}</span>
    </button>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
  name: 'AssetItemList',

  components: { draggable },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    selectedId: {
      type: [Number, String],
      default: null,
    },
    title: {
      type: String,
      default: 'Items',
    },
    addLabel: {
      type: String,
      default: 'Add',
    },
    nameFallback: {
      type: String,
      default: 'Untitled',
    },
    emptyCopy: {
      type: String,
      default: 'Nothing here yet.',
    },
    // When set, each row shows a dot filled in whenever `item[enabledKey]` is
    // truthy, so an asset that affiliates cannot see is obvious from the list.
    enabledKey: {
      type: String,
      default: '',
    },
    errorIds: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      // vuedraggable mutates the array it is given, so it gets a shallow copy
      // rather than the parent's own list; the parent is told about the new
      // order through `reorder` and stays in charge of applying it.
      localItems: this.items.slice(),
    };
  },

  watch: {
    // Reading `this.items` in the watcher also subscribes to the array's own
    // observer, so this fires for the in-place push/splice/sort the tabs do as
    // well as for a wholesale replacement.
    items: 'syncLocalItems',
  },

  methods: {
    syncLocalItems() {
      this.localItems = this.items.slice();
    },

    // Ids come back from the API as whatever JSON held, so they are compared as
    // strings rather than assuming both sides are numbers.
    isSelected(item) {
      return this.selectedId !== null
        && this.selectedId !== ''
        && String(item.id) === String(this.selectedId);
    },

    hasError(item) {
      return this.errorIds.some(id => String(id) === String(item.id));
    },

    onReorder(orderedItems) {
      this.$emit('reorder', orderedItems.map((item) => item.id));
    },
  },
};
</script>

<style scoped>
.gs-item-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  align-self: start;
}

.gs-item-list__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 4px;
  color: var(--gs-muted-foreground);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
}

.gs-item-list__count {
  padding: 2px 7px;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  background: var(--gs-muted);
  border-radius: 999px;
}

.gs-item-list__empty {
  margin: 0;
  padding: 12px;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
  background: var(--gs-surface-subtle);
  border: 1px dashed var(--gs-border-strong);
  border-radius: var(--gs-radius-md);
}

.gs-item-list__rows {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.gs-item-list__row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 40px;
  padding: 6px 10px;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25;
  text-align: left;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  /* Kept at 3px in every state so the selected rail does not shift the row. */
  border-left: 3px solid transparent;
  border-radius: var(--gs-radius-md);
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease;
}

.gs-item-list__row:hover {
  background: var(--gs-muted);
}

.gs-item-list__row:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-item-list__row.has-error {
  border-color: var(--gs-danger-border);
}

/* Same "you are here" language as the asset-type sidebar: primary rail, soft
   primary fill, bold label. Declared after `.has-error` so a row that is both
   keeps the danger outline and still shows the rail. */
.gs-item-list__row.is-selected {
  font-weight: 700;
  background: var(--gs-primary-soft);
  border-left-color: var(--gs-primary);
}

.gs-item-list__row.is-ghost {
  opacity: 0.4;
  background: var(--gs-primary-soft);
}

.gs-item-list__grip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 20px;
  height: 28px;
  color: var(--gs-muted-foreground);
  cursor: grab;
}

.gs-item-list__grip:active {
  cursor: grabbing;
}

.gs-item-list__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border: 1.5px solid var(--gs-border-strong);
  border-radius: 999px;
  background: transparent;
}

.gs-item-list__dot.is-on {
  border-color: var(--gs-success);
  background: var(--gs-success);
}

.gs-item-list__name {
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-item-list__row.has-error .gs-item-list__name {
  color: var(--gs-danger);
}

/* The kit has no chevron-right, so the down chevron is turned a quarter turn. */
.gs-item-list__chevron {
  flex: 0 0 auto;
  color: var(--gs-muted-foreground);
  transform: rotate(-90deg);
}

.gs-item-list__add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 38px;
  padding: 6px 12px;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  background: transparent;
  border: 1px dashed var(--gs-border-strong);
  border-radius: var(--gs-radius-md);
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease;
}

.gs-item-list__add:hover {
  background: var(--gs-muted);
  border-color: var(--gs-primary);
}

.gs-item-list__add:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}
</style>
